import React from 'react';
import { FieldInputProps } from 'react-final-form';
import { FieldState } from 'final-form';
import { FormSchemaEntry, PlainTextProps } from './index';
import Input, { InputCurrency, InputNumber, InputPhone } from '@gik/ui/Input';
import Checkbox from '@gik/ui/Checkbox';
import TextArea from '@gik/ui/TextArea';
import SelectTime from '@gik/ui/Select/SelectTime';
import { UIVariant, UISize } from '../types';
import noop from '@gik/core/utils/noop';
import ImagePicker from '../ImagePicker';
import Select, { SelectUSStates } from '../Select';
import { GeoSuggestInput, YesNoButtonInput } from '@gik/ui';
import { RadioSelect, RadioSelectProps } from '../Radio';
import SelectList from '../SelectList';
import { AnyType } from '../Table';
import { CalendarEventTypeSelect, CalendarEventTypeSelectList } from '@gik/calendar';
import WeekdaySelector from '../WeekdaySelector/WeekdaySelector';
import moment from 'moment-timezone';
import { InputDatePicker } from '../DatePicker/InputDatePicker';
import { Moment } from 'moment';

interface generateFieldArgs {
  entry: FormSchemaEntry;
  formFieldProps: FieldInputProps<unknown, HTMLElement>;
  fieldState?: Partial<FieldState<object>>;
  variant?: UIVariant;
  size?: UISize;
  disabled?: boolean;
  schemaComponentProps?: object;
  extraProps?: object;
  onChange?(value: AnyType): void;
}

export function generateField({
  entry,
  formFieldProps,
  disabled,
  schemaComponentProps,
  fieldState,
  extraProps,
  onChange = noop,
}: generateFieldArgs) {
  const fieldType = entry.type;
  const mainProps = {
    key: formFieldProps.key,
    name: entry.name,
    placeholder: entry.placeholder,
    maxLength: entry.maxLength,
    hasError: fieldState ? fieldState.invalid && fieldState.touched : false,
    disabled: disabled || formFieldProps.disabled,
    readOnly: entry.readOnly,
    onBlur: formFieldProps.onBlur,
    onFocus: formFieldProps.onFocus,
    onChange: (value: AnyType) => {
      if (value instanceof Event) {
        onChange((value.target as HTMLInputElement).value);
      } else {
        onChange(value);
      }
      formFieldProps.onChange(value);
    },
    ...schemaComponentProps,
    ...(entry.props as object),
  };

  let _field: React.ReactElement;

  const style = {
    width: entry.width,
  };

  switch (fieldType) {
    case 'hidden':
      _field = <Input style={style} {...mainProps} value={entry.default as string} type="hidden" />;
      break;
    case 'plaintext':
      _field = (
        <div
          style={{
            ...((entry.props as PlainTextProps)?.centerVertical
              ? { marginTop: '6px' /* TODO: apply through sass */ }
              : {}),
          }}
        >
          {formFieldProps.value || entry.text}
          <Input style={style} {...mainProps} value={(formFieldProps.value || entry.default) as string} type="hidden" />
        </div>
      );
      break;
    case 'checkbox':
      _field = <Checkbox style={style} {...mainProps} checked={formFieldProps.checked as boolean} />;
      break;
    case 'textarea':
      _field = <TextArea style={style} {...mainProps} value={formFieldProps.value as string} />;
      break;
    case 'select':
      _field = (
        <Select
          block
          style={style}
          variant="default"
          {...mainProps}
          value={formFieldProps.value as string}
          onChange={(value: string) => {
            formFieldProps.onChange(value);
          }}
        />
      );
      break;
    case 'select-time':
      _field = (
        <SelectTime
          block
          style={style}
          variant="default"
          {...mainProps}
          value={formFieldProps.value as number}
          onChange={value => {
            formFieldProps.onChange(value);
          }}
        />
      );
      break;
    case 'radio-select':
      _field = (
        <RadioSelect
          style={style}
          variant="default"
          {...(mainProps as typeof mainProps & RadioSelectProps)}
          value={formFieldProps.value as string}
        />
      );
      break;
    case 'select-us-states':
      _field = (
        <SelectUSStates
          block
          style={style}
          variant="default"
          {...mainProps}
          value={formFieldProps.value as string}
          onChange={(value: string) => {
            formFieldProps.onChange(value);
          }}
        />
      );
      break;
    case 'select-calendar-event-type':
      _field = (
        <CalendarEventTypeSelect
          block
          style={style}
          variant="default"
          {...mainProps}
          value={formFieldProps.value as number}
          onChange={(value: number) => {
            formFieldProps.onChange(value);
          }}
        />
      );
      break;
    case 'date-picker':
      _field = (
        <InputDatePicker
          {...mainProps}
          {...extraProps}
          value={formFieldProps.value ? moment(formFieldProps.value as string) : null}
          onChange={(date: Moment) => {
            if (!date) return;
            formFieldProps.onChange(date);
          }}
        />
      );
      break;
    case 'currency':
      delete mainProps.onChange;
      _field = (
        <InputCurrency
          style={style}
          {...mainProps}
          value={formFieldProps.value as string}
          onValueChange={formFieldProps.onChange}
          onChange={noop}
        />
      );
      break;
    case 'formatted-number':
      delete mainProps.onChange;
      _field = (
        <InputNumber
          style={style}
          {...mainProps}
          value={formFieldProps.value as string}
          onValueChange={formFieldProps.onChange}
          onChange={noop}
        />
      );
      break;
    case 'phone':
      delete mainProps.onChange;
      _field = (
        <InputPhone
          style={style}
          {...mainProps}
          value={formFieldProps.value as string}
          onValueChange={formFieldProps.onChange}
          onChange={noop}
        />
      );
      break;
    case 'image-picker':
      _field = <ImagePicker style={style} {...mainProps} value={formFieldProps.value as string} />;
      break;
    case 'geosuggest':
      _field = <GeoSuggestInput {...mainProps} />;
      break;
    case 'select-list':
      delete mainProps.hasError;
      _field = (
        <SelectList<unknown> cols={1} {...mainProps} value={formFieldProps.value} render={entry.props['render']} />
      );
      break;
    case 'select-list-event-type':
      delete mainProps.hasError;
      _field = <CalendarEventTypeSelectList {...mainProps} />;
      break;
    case 'yes-no':
      _field = (
        <YesNoButtonInput
          {...mainProps}
          onChange={value => {
            formFieldProps.onChange(value);
          }}
          value={formFieldProps.value as boolean}
        />
      );
      break;
    case 'weekday-selector':
      delete mainProps.hasError;
      _field = <WeekdaySelector {...mainProps} value={formFieldProps.value as boolean[]} />;
      break;

    // all other cases should be handled as a normal input field
    default:
      _field = <Input style={style} {...mainProps} value={formFieldProps.value as string} type={fieldType as string} />;
  }
  return _field;
}
